import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useWebinarT } from '@/hooks/useWebinarT';
import { useSubmitLock } from '@/hooks/useSubmitLock';
import {
  fetchPublicWebinarSession,
  fetchPublicWebinarSubscriptionForm,
  registerPublicWebinar,
  registerPublicWebinarReplay,
} from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import type { PublicWebinarSession, PublicWebinarSubscriptionForm } from '@/features/webinar/types';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { InputLabel } from '@/components/ui/InputLabel';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Clock } from 'lucide-react';
import { getFormattedDate } from '@/lib/utils';
import { isPublicSigninClosed } from '@/lib/publicSigninAccess';
import { Skeleton } from '@/components/ui/skeleton';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import SplashScreen from '@/components/common/SplashScreen';
import LiveEntryError from '@/components/common/LiveEntryError';
import { reportPublicSessionUnavailable } from '@/lib/publicSessionRead';
import { PhoneSelectInput } from '@/components/ui/PhoneSelectInput';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { toWebinarLng } from '@/lib/webinarLanguage';

type PublicSubscriptionFormConfig = PublicWebinarSubscriptionForm & {
  firstNameField?: boolean;
  lastNameField?: boolean;
  emailField?: boolean;
  phoneField?: boolean;
  firstNameMandatory?: boolean;
  lastNameMandatory?: boolean;
  emailMandatory?: boolean;
  phoneMandatory?: boolean;
};

function buildSubscriptionSchema(
  form: PublicSubscriptionFormConfig | null | undefined,
  t: (key: string) => string,
) {
  const schemaObject: Record<string, z.ZodTypeAny> = {};

  if (form?.firstNameField) {
    schemaObject.firstName = form.firstNameMandatory
      ? z.string().min(1, t('publicSignin.validation.firstNameRequired'))
      : z.string().optional();
  }

  if (form?.lastNameField) {
    schemaObject.lastName = form.lastNameMandatory
      ? z.string().min(1, t('publicSignin.validation.lastNameRequired'))
      : z.string().optional();
  }

  if (form?.emailField) {
    schemaObject.email = form.emailMandatory
      ? z
          .string()
          .email(t('publicSignin.validation.emailInvalid'))
          .min(1, t('publicSignin.validation.emailRequired'))
      : z
          .union([z.string().email(t('publicSignin.validation.emailInvalid')), z.literal('')])
          .optional();
  }

  if (form?.phoneField) {
    schemaObject.phone = form.phoneMandatory
      ? z
          .string()
          .min(1, t('publicSignin.validation.phoneRequired'))
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return isValidPhoneNumber(val);
            },
            { message: t('publicSignin.validation.phoneInvalid') },
          )
      : z
          .string()
          .optional()
          .refine(
            (val) => {
              if (!val || val.trim() === '') return true;
              return isValidPhoneNumber(val);
            },
            { message: t('publicSignin.validation.phoneInvalid') },
          );
  }

  return z.object(schemaObject);
}

export type SigninAttendeeMode = 'live' | 'replay';

type SigninAttendeeRegistrationFormProps = {
  subscriptionForm: PublicSubscriptionFormConfig;
  webinarId: string;
  sessionIdentifier: string;
  visualIdentity: PublicWebinarSession['visualIdentity'] | null;
  mode: SigninAttendeeMode;
};

function SigninAttendeeRegistrationForm({
  subscriptionForm,
  webinarId,
  sessionIdentifier,
  visualIdentity,
  mode,
}: SigninAttendeeRegistrationFormProps) {
  const { t } = useWebinarT('webinars');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const registerLoading = useAppSelector(
    (state: RootState) => state.webinar.publicWebinarRegisterLoading,
  );
  const registerError = useAppSelector(
    (state: RootState) => state.webinar.publicWebinarRegisterError,
  );
  const { isLocked, runExclusive } = useSubmitLock();

  const schema = useMemo(
    () => buildSubscriptionSchema(subscriptionForm, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionForm],
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    mode: 'onChange',
  });

  const submitRegistration = async (data: FormValues) => {
    const registrationData = {
      firstName: typeof data.firstName === 'string' ? data.firstName : undefined,
      lastName: typeof data.lastName === 'string' ? data.lastName : undefined,
      email: typeof data.email === 'string' ? data.email : undefined,
      phoneNumber: typeof data.phone === 'string' ? data.phone : undefined,
    };

    try {
      if (mode === 'replay') {
        const result = await dispatch(
          registerPublicWebinarReplay({
            webinarId: Number(webinarId),
            sessionIdentifier,
            data: registrationData,
          }),
        );

        if (registerPublicWebinarReplay.fulfilled.match(result) && result.payload?.token) {
          navigate(`/live/${webinarId}/${sessionIdentifier}?token=${result.payload.token}`);
        }
        return;
      }

      const result = await dispatch(
        registerPublicWebinar({
          data: {
            sessionIdentifier,
            ...registrationData,
          },
        }),
      );

      if (registerPublicWebinar.fulfilled.match(result) && result.payload?.token) {
        navigate(`/waiting-room/${webinarId}/${sessionIdentifier}?token=${result.payload.token}`);
      }
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  const onSubmit = (data: FormValues) => runExclusive(() => submitRegistration(data));

  const theme = visualIdentity?.theme as 'LIGHT' | 'DARK' | undefined;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6 pb-8">
        {subscriptionForm.firstNameField && (
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputLabel
                    label={`${t('publicSignin.firstName')}${subscriptionForm.firstNameMandatory ? ' *' : ''}`}
                    id="firstName"
                    type="text"
                    value={(field.value as string) || ''}
                    onChange={field.onChange}
                    theme={theme}
                  />
                </FormControl>
                <FormMessage className="text-left text-red-500 text-sm" />
              </FormItem>
            )}
          />
        )}
        {subscriptionForm.lastNameField && (
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputLabel
                    label={`${t('publicSignin.lastName')}${subscriptionForm.lastNameMandatory ? ' *' : ''}`}
                    id="lastName"
                    type="text"
                    value={(field.value as string) || ''}
                    onChange={field.onChange}
                    theme={theme}
                  />
                </FormControl>
                <FormMessage className="text-left text-red-500 text-sm" />
              </FormItem>
            )}
          />
        )}
        {subscriptionForm.emailField && (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputLabel
                    label={`${t('publicSignin.email')}${subscriptionForm.emailMandatory ? ' *' : ''}`}
                    id="email"
                    type="email"
                    value={(field.value as string) || ''}
                    onChange={field.onChange}
                    theme={theme}
                  />
                </FormControl>
                <FormMessage className="text-left text-red-500 text-sm" />
              </FormItem>
            )}
          />
        )}
        {subscriptionForm.phoneField && (
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PhoneSelectInput
                    defaultCountry="FR"
                    value={field.value as string | undefined}
                    onChange={(value) => {
                      field.onChange(value || '');
                    }}
                    label={
                      subscriptionForm.phoneMandatory
                        ? `${t('publicSignin.phone')} *`
                        : t('publicSignin.phoneOptional')
                    }
                    theme={theme}
                  />
                </FormControl>
                <FormMessage className="text-left text-red-500 text-sm" />
              </FormItem>
            )}
          />
        )}
        {registerError && <p className="text-red-500 text-sm text-center">{registerError}</p>}
        <Button
          type="submit"
          className="hover:opacity-90 mx-auto min-h-10 min-w-[248px] max-w-full w-fit px-6 py-3 whitespace-normal wrap-break-word text-center transition-opacity"
          disabled={registerLoading || isLocked}
          style={{
            backgroundColor: visualIdentity?.accentColor,
          }}
        >
          {registerLoading || isLocked
            ? t('publicSignin.registering')
            : subscriptionForm.ctaButtonLabel ||
              (mode === 'replay' ? t('publicSignin.registerReplay') : t('publicSignin.register'))}
        </Button>
      </form>
    </Form>
  );
}

type SigninAttendeePageProps = {
  mode?: SigninAttendeeMode;
};

export default function SigninAttendeePage({ mode = 'live' }: SigninAttendeePageProps) {
  const { t } = useWebinarT('webinars');
  const { webinarId, sessionIdentifier } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();

  const dispatch = useAppDispatch();

  const currentPublicSubscriptionForm = useAppSelector(
    (state: RootState) => state.webinar.currentPublicSubscriptionForm,
  );
  const currentPublicWebinarSession = useAppSelector(
    (state: RootState) => state.webinar.currentPublicSession,
  );
  const publicSessionLoading = useAppSelector(
    (state: RootState) => state.webinar.publicSessionLoading,
  );
  const publicSubscriptionFormLoading = useAppSelector(
    (state: RootState) => state.webinar.publicSubscriptionFormLoading,
  );
  const publicSessionError = useAppSelector((state: RootState) => state.webinar.publicSessionError);
  const publicSessionErrorTransient = useAppSelector(
    (state: RootState) => state.webinar.publicSessionErrorTransient,
  );
  useEffect(() => {
    if (!webinarId || !sessionIdentifier) {
      return;
    }

    dispatch(
      fetchPublicWebinarSession({
        webinarId: Number(webinarId),
        sessionIdentifier,
      }),
    );
    dispatch(
      fetchPublicWebinarSubscriptionForm({
        webinarId: Number(webinarId),
      }),
    );
  }, [webinarId, sessionIdentifier, dispatch]);

  const publicSessionUnavailable =
    !publicSessionLoading && Boolean(publicSessionError) && Boolean(publicSessionErrorTransient);

  useEffect(() => {
    if (!publicSessionUnavailable) return;
    reportPublicSessionUnavailable({
      page: 'signin',
      webinarId,
      sessionIdentifier,
      error: publicSessionError ?? null,
    });
  }, [publicSessionUnavailable, webinarId, sessionIdentifier, publicSessionError]);

  const retryPublicSession = useCallback(() => {
    if (!webinarId || !sessionIdentifier) return;
    dispatch(fetchPublicWebinarSession({ webinarId: Number(webinarId), sessionIdentifier }));
  }, [webinarId, sessionIdentifier, dispatch]);

  const visualIdentity = currentPublicWebinarSession?.visualIdentity;
  const plannedDate = currentPublicWebinarSession?.session.utcPlannedDate;

  const isSessionClosed = useMemo(() => {
    if (mode === 'replay' || !currentPublicWebinarSession) {
      return false;
    }

    return isPublicSigninClosed(currentPublicWebinarSession.session);
  }, [currentPublicWebinarSession, mode]);

  const isReplayUnavailable = useMemo(() => {
    if (mode !== 'replay' || !currentPublicWebinarSession) {
      return false;
    }

    const { status, replayEnabled } = currentPublicWebinarSession.session;
    return status !== 'FINISHED' || !replayEnabled;
  }, [currentPublicWebinarSession, mode]);

  const webinarLanguage = currentPublicWebinarSession?.language;

  const sessionTimezone = currentPublicWebinarSession?.session.timezone;
  const formattedPlannedDate = useMemo(() => {
    if (!plannedDate) return '';
    return getFormattedDate(plannedDate, sessionTimezone, toWebinarLng(webinarLanguage));
  }, [plannedDate, webinarLanguage, sessionTimezone]);

  const containerBackgroundStyle = useMemo(() => {
    const backgroundType = currentPublicSubscriptionForm?.background ?? 'DEFAULT';
    const backgroundColor = currentPublicSubscriptionForm?.backgroundColor;
    const backgroundGradient = currentPublicSubscriptionForm?.backgroundGradient;
    const backgroundImageUrl = currentPublicSubscriptionForm?.backgroundImageUrl || undefined;

    if (backgroundType === 'IMAGE') {
      const decodedUrl = backgroundImageUrl ? decodeURIComponent(backgroundImageUrl) : undefined;
      return {
        backgroundColor: visualIdentity?.backgroundColor || '#FBFBFB',
        backgroundImage: decodedUrl ? `url(${decodedUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      } as React.CSSProperties;
    }

    if (backgroundType === 'GRADIENT' && visualIdentity) {
      if (backgroundGradient === '2') {
        return {
          background: `linear-gradient(226deg, ${visualIdentity.gradient2Color1 || '#000'} 0.2%, ${visualIdentity.gradient2Color2 || '#fff'} 104.63%)`,
        } as React.CSSProperties;
      }

      return {
        background: `linear-gradient(to bottom, ${visualIdentity.gradient1Color1 || '#000'}, ${visualIdentity.gradient1Color2 || '#fff'})`,
      } as React.CSSProperties;
    }

    if (backgroundType === 'DEFAULT' && visualIdentity) {
      return {
        backgroundColor: visualIdentity.backgroundColor,
      } as React.CSSProperties;
    }

    return {
      backgroundColor:
        (backgroundColor && backgroundColor.trim().length > 0
          ? backgroundColor
          : visualIdentity?.backgroundColor) || '#FBFBFB',
    } as React.CSSProperties;
  }, [currentPublicSubscriptionForm, visualIdentity]);

  const shouldShowSkeleton =
    (publicSessionLoading || publicSubscriptionFormLoading) &&
    (!currentPublicWebinarSession || !currentPublicSubscriptionForm);

  if (shouldShowSkeleton) {
    return (
      <div className="w-full h-full min-h-screen relative flex items-center justify-center bg-background">
        <div className="p-8 max-w-[700px] my-16 rounded-[20px] space-y-8 flex flex-col items-center text-center bg-medium">
          <Skeleton className="h-16 w-40 rounded-xl" />
          <div className="flex flex-col gap-4 items-center w-full">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-80" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-40 mx-auto" />
          </div>
        </div>
      </div>
    );
  }
  if (publicSessionUnavailable) {
    return <LiveEntryError step="session" onRetry={retryPublicSession} />;
  }
  if (!publicSessionLoading && (publicSessionError || isSessionClosed)) {
    return <SplashScreen label={t('publicSignin.sessionClosed')} fullscreen />;
  }
  if (!publicSessionLoading && isReplayUnavailable) {
    return <SplashScreen label={t('publicSignin.replayUnavailable')} fullscreen />;
  }
  return (
    <div
      className="w-full h-full min-h-screen relative flex items-center justify-center"
      style={containerBackgroundStyle}
    >
      {/* logo welya */}
      <div className="absolute top-4 right-4 p-4">
        <PoweredByLogo
          theme={visualIdentity?.theme as 'LIGHT' | 'DARK' | undefined}
          fontFamily={visualIdentity?.fontFamily}
          isNotLivePage={true}
        />
      </div>

      <div
        className="p-8 max-w-[700px] my-16 rounded-[20px] space-y-8 flex flex-col items-center text-center"
        style={{
          backgroundColor:
            visualIdentity?.theme === 'LIGHT'
              ? 'rgba(31, 31, 31, 0.03)'
              : 'rgba(255, 255, 255, 0.1)',
        }}
      >
        {visualIdentity?.logoUrl && currentPublicSubscriptionForm?.displayLogo && (
          <img
            src={visualIdentity?.logoUrl}
            alt="logo"
            width={140}
            height={64}
            className="object-contain"
          />
        )}

        <div className="flex flex-col gap-6 items-center justify-center">
          <h1
            className={`text-2xl font-bold ${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
            style={{
              fontFamily: visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {currentPublicWebinarSession?.session.webinarTitle}
          </h1>
          <p
            className={`text-sm font-normal ${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
            style={{
              fontFamily: visualIdentity?.fontFamily || 'Poppins',
            }}
          >
            {currentPublicWebinarSession?.session.webinarShortDescription}
          </p>

          <div className="flex flex-col gap-4">
            {currentPublicSubscriptionForm?.displaySpeakers &&
              currentPublicWebinarSession?.session.presenters?.map((presenter, index) => (
                <div
                  key={presenter?.fullname + presenter?.pictureUrl + index}
                  className="flex items-center gap-4"
                >
                  <Avatar className="size-12">
                    <AvatarImage src={presenter.pictureUrl} />
                    <AvatarFallback>
                      {presenter.fullname
                        ?.split(' ')
                        .map((n) => n.charAt(0))
                        .join('')
                        .toUpperCase()
                        .slice(0, 2) ?? '?'}
                    </AvatarFallback>
                  </Avatar>

                  <div>
                    <p
                      className={visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}
                      style={{
                        fontFamily: visualIdentity?.fontFamily || 'Poppins',
                      }}
                    >
                      {t('publicSignin.presenterBy')}
                      <span
                        className=" text-xl font-semibold"
                        style={{
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {presenter.fullname}
                      </span>{' '}
                    </p>
                  </div>
                </div>
              ))}
          </div>

          <div
            className="border p-4 rounded-2xl w-full flex items-center justify-center"
            style={{
              borderColor: visualIdentity?.contrastColor,

              backgroundColor:
                visualIdentity?.theme === 'LIGHT'
                  ? 'rgba(31, 31, 31, 0.03)'
                  : 'rgba(255, 255, 255, 0.1)',
            }}
          >
            <Clock className="size-6 text-[#9E9FA6] mr-2" />
            <div
              className={`text-base font-semibold mr-2 ${visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {mode === 'replay'
                ? t('publicSignin.replayDate', { date: formattedPlannedDate })
                : formattedPlannedDate}
            </div>
          </div>
        </div>

        <div className="w-full">
          {currentPublicSubscriptionForm && webinarId && sessionIdentifier && (
            <SigninAttendeeRegistrationForm
              key={`${webinarId}-${sessionIdentifier}-${mode}`}
              subscriptionForm={currentPublicSubscriptionForm as PublicSubscriptionFormConfig}
              webinarId={webinarId}
              sessionIdentifier={sessionIdentifier}
              visualIdentity={visualIdentity ?? null}
              mode={mode}
            />
          )}
        </div>

        <p
          className={visualIdentity?.theme === 'DARK' ? 'text-white' : 'text-black'}
          style={{
            fontFamily: visualIdentity?.fontFamily || 'Poppins',
          }}
        >
          {t('publicSignin.disclaimer')}
        </p>
      </div>
    </div>
  );
}
